﻿import React from "react";
import { useNavigate } from "react-router-dom";
import { useUserVipStatus } from "../hooks/useUserVipStatus";
import { Award } from "lucide-react";

/**
 * Reusable VIP Progress Component
 * Shows current tier, progress bar, and next tier information
 */
const VipProgress = ({
  className = "",
  variant = "default", // 'default' | 'sidebar'
}) => {
  const navigate = useNavigate();
  const {
    loading,
    error,
    currentTier,
    nextTier,
    progressPercentage,
    currentFeathersBalance,
  } = useUserVipStatus();

  // Loading state
  if (loading) {
    return (
      <div className={`bg-card/30 rounded-lg p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-card rounded mb-2"></div>
          <div className="h-3 bg-card rounded mb-2"></div>
          <div className="h-3 bg-card rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`bg-card/30 rounded-lg p-4 ${className}`}>
        <div className="text-red-400 text-sm">Error loading VIP data</div>
      </div>
    );
  }

  // Max tier reached - but only show this if we're actually at a high tier, not just Egg
  if (!nextTier) {
    // If we're at Egg tier (tier_order 0) and there's no next tier, it means VIP system has issues
    // Show Egg tier normally instead of "Max Tier" message
    if (
      currentTier?.tier_order === 0 ||
      currentTier?.name === "egg" ||
      currentTier?.display_name === "Egg"
    ) {
      // Show Egg tier as normal tier, not as "max tier reached"
      // This will fall through to the normal component below
    } else {
      // Actually at max tier (not Egg) - only show if tier_order > 0
      if (variant === "sidebar" && currentTier?.tier_order > 0) {
        return (
          <div className={`bg-card/30 rounded-lg p-4 ${className}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                VIP Status
              </span>
            </div>
            <div className="text-sm font-bold mb-2">
              {currentTier?.display_name || "No Tier"}
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold">
                Max Tier!
              </span>
            </div>
          </div>
        );
      }

      // Only show max tier message if actually at a high tier (tier_order > 0)
      if (currentTier?.tier_order > 0) {
        return (
          <div
            className={`bg-gradient-to-r from-yellow-600/20 to-primary/20 border border-primary/30 rounded-lg p-4 text-center ${className}`}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Award className="w-5 h-5 text-primary" />
              <span className="text-primary font-semibold">
                Maximum VIP Tier Reached!
              </span>
            </div>
            <p className="text-foreground text-sm">
              You've achieved the highest VIP status. Enjoy all the exclusive
              benefits!
            </p>
          </div>
        );
      }
    }
  }

  // Single reusable component - used everywhere
  return (
    <div className={`bg-card/30 rounded-lg p-4 ${className}`}>
      {/* Header with tier name and percentage */}
      <div className="flex items-center justify-between mb-3">
        {variant === "sidebar" ? (
          <button
            type="button"
            onClick={() => navigate("/vip")}
            className="text-white font-semibold text-lg text-left hover:text-foreground focus:outline-none"
          >
            VIP: {currentTier?.display_name || "No Tier"}
          </button>
        ) : (
          <div className="text-white font-semibold text-lg">
            VIP: {currentTier?.display_name || "No Tier"}
          </div>
        )}
        <div className="text-primary text-sm">
          {progressPercentage.toFixed(1)}%
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-secondary rounded-full h-3 mb-3">
        <div
          // eslint-disable-next-line no-restricted-syntax -- intentional 3-stop brand gradient on VIP-progress fill
          className="bg-gradient-to-r from-[#FFE48A] via-[#F5B955] to-[#A87A1F] h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.max(2, progressPercentage)}%` }}
        ></div>
      </div>

      {/* Bottom info */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          Next: {nextTier?.display_name || "Max Tier"}
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <img
            src="/assets/3d/feather-transparent.png"
            alt="Feathers"
            className="w-4 h-4"
          />
          <span>
            {(currentFeathersBalance || 0).toLocaleString()} /{" "}
            {(nextTier?.feathers_required || 0).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VipProgress;
